import { BILLING_PLANS } from '@/lib/billing/plans'
import { defaultPriceSummary } from '@/lib/billing/price-summary'
import { BREW_DEMO_BOOKING_URL } from '@/lib/site/booking'
import { marketingContent } from '@/lib/site/marketing-content'
import { POSITIONING } from '@/lib/site/messaging'
import { hasPublicMarkdownTwin } from '@/lib/site/seo/markdown-paths'

/** When unset, use localhost in `next dev` so metadataBase and canonical URLs match the dev server. */
const DEFAULT_SITE_URL =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:3000'
    : 'https://brew.new'

/**
 * Ensure URL has a protocol prefix
 */
function ensureProtocol(url: string): string {
  if (!url) {
    return DEFAULT_SITE_URL
  }
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }
  return `https://${url}`
}

export const siteConfig = {
  name: 'Brew',
  description: POSITIONING.tagline,
  cta: 'Get Started',
  url: ensureProtocol(process.env.NEXT_PUBLIC_APP_URL || DEFAULT_SITE_URL),

  // =============================================================================
  // SEO CONFIGURATION - Centralized metadata for all pages
  // =============================================================================
  seo: {
    // Base metadata
    title: {
      default: POSITIONING.homeTitle,
      template: '%s | Brew',
    },
    description: POSITIONING.shortMeta,

    // Page-specific metadata (DRY reference)
    pages: {
      home: {
        title: POSITIONING.homeTitle,
        description: POSITIONING.shortMeta,
        path: '/',
        absoluteTitle: true,
      },
      pricing: {
        title: 'Pricing: AI-Native ESP Plans',
        description:
          'Transparent pricing for the AI-native ESP. Every plan includes all features. Start free with 500 AI credits and 1,000 sends a month.',
        path: '/pricing',
      },
      mcp: {
        title: 'MCP Server: Email Marketing for AI Agents',
        description:
          'Connect ChatGPT, Claude, Cursor, or any AI agent to Brew over MCP. Agents generate, send, automate, and analyze on-brand email.',
        path: '/mcp',
      },
      marketers: {
        title: 'Email Marketing for Marketers',
        description:
          'On-brand campaigns from one prompt. Brew learns your brand, designs every email, and sends it from your own domain, with results in real time.',
        path: '/marketers',
      },
      startups: {
        title: 'Email Marketing for Startups',
        description:
          'Product email from your agent, your API, and your stack. Ship onboarding, launches, and lifecycle email without an email team.',
        path: '/startups',
      },
      agencies: {
        title: 'Email Marketing for Agencies',
        description:
          "Every client's brand in one workspace. Build on brand for each client, review together in one thread, and ship client campaigns faster.",
        path: '/agencies',
      },
      ecommerce: {
        title: 'Email Marketing for Ecommerce',
        description:
          'Campaigns and flows from your Shopify store. Brew designs beautiful, on-brand emails that convert, and tracks every send in real time.',
        path: '/ecommerce',
      },
      developers: {
        title: 'Brew.new developer resources',
        description:
          'Brew.new developer resources: Public API v1, OpenAPI spec, authentication, webhooks, TypeScript SDK, and hosted MCP server.',
        path: '/developers',
        absoluteTitle: true,
      },
      support: {
        title: 'Support',
        description:
          'Get help with Brew in ChatGPT, Claude, Grok, Cursor, Codex, and other MCP clients, plus support for your email design and sending workflows.',
        path: '/support',
      },
      blog: {
        title: 'Blog: Email Marketing, AI & Growth',
        description:
          'Product updates and notes from the Brew team on email marketing, lifecycle emails, and building with AI agents over MCP.',
        path: '/blog',
      },
      integrations: {
        title: 'Integrations: Connect Your Stack',
        description:
          'One-click OAuth and API-key integrations for the tools you already use. Pull design files and payments in; push polished emails into your ESP.',
        path: '/browse/integrations',
      },
      audience: {
        title: 'Audience',
        description: 'Manage your email audience and contacts with Brew.',
        path: '/audience',
      },
      automations: {
        title: 'Automations',
        description:
          'Build smart email workflows that run on autopilot with AI-powered automation.',
        path: '/automations',
      },
      emails: {
        title: 'Emails',
        description: 'View and manage all your emails in one place.',
        path: '/emails',
      },
      analytics: {
        title: 'Analytics',
        description:
          'Track email performance with real-time analytics and AI-powered insights.',
        path: '/analytics',
      },
      brand: {
        title: 'Brand Settings',
        description:
          'Configure your brand colors, logos, and fonts for consistent email design.',
        path: '/brand',
      },
      inbound: {
        title: 'Inbound Emails',
        description: 'Manage and organize your inbound email communications.',
        path: '/inbound',
      },
    },

    // OpenGraph defaults
    openGraph: {
      type: 'website' as const,
      locale: 'en_US',
      siteName: 'Brew',
      images: {
        default: {
          url: '/opengraph-image',
          width: 1200,
          height: 630,
          alt: 'Brew: AI-Native ESP for Modern Teams and AI Agents',
        },
      },
    },

    // Twitter defaults
    twitter: {
      card: 'summary_large_image' as const,
      creator: '@brewdotnew',
      site: '@brewdotnew',
    },
  },

  // =============================================================================
  // CONTACT INFORMATION - Centralized contact details
  // =============================================================================
  contact: {
    email: {
      support: 'support@brew.new',
      founders: 'founders@brew.new',
      sales: 'founders@brew.new',
    },
    phone: '+1-(332)-203-2145',
    scheduling: BREW_DEMO_BOOKING_URL,
  },

  // =============================================================================
  // SOCIAL LINKS - Consolidated social media links
  // =============================================================================
  social: {
    twitter: 'https://x.com/brewdotnew',
    twitterHandle: '@brewdotnew',
    youtube: 'https://www.youtube.com/@brewdotnew',
    linkedin: 'https://www.linkedin.com/company/brewdotnew',
  },

  // =============================================================================
  // JSON-LD SCHEMA TEMPLATES - Structured data for search engines
  // =============================================================================
  jsonLd: {
    organization: {
      '@type': 'Organization' as const,
      '@id': `${DEFAULT_SITE_URL}/#organization`,
      name: 'Brew',
      url: DEFAULT_SITE_URL,
      logo: `${DEFAULT_SITE_URL}/logo/favicon-dark.svg`,
      sameAs: [
        'https://twitter.com/brewdotnew',
        'https://linkedin.com/company/brewdotnew',
        'https://youtube.com/@brewdotnew',
      ],
    },
    software: {
      '@type': 'SoftwareApplication' as const,
      name: 'Brew',
      description: POSITIONING.categorySentence,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: {
        '@type': 'Offer' as const,
        price: '0',
        priceCurrency: 'USD',
      },
    },
    website: {
      '@type': 'WebSite' as const,
      '@id': `${DEFAULT_SITE_URL}/#website`,
      url: DEFAULT_SITE_URL,
      name: 'Brew',
    },
  },
  ...marketingContent,
}
// =============================================================================
// HELPER FUNCTIONS - Utilities for generating metadata from config
// =============================================================================

export function getSiteUrl(): string {
  return siteConfig.url.replace(/\/$/, '')
}

export function getAbsoluteUrl(path = ''): string {
  const siteUrl = getSiteUrl()

  if (!path) {
    return siteUrl
  }

  return `${siteUrl}${path.startsWith('/') ? path : `/${path}`}`
}

/** Absolute `text/markdown` twin for a public path (`/` → `/index.md`). */
export function getMarkdownAlternateHref(path: string): string {
  const normalized =
    path === '/' || path === '' ? '/index' : path.replace(/\/$/, '')
  return `${getSiteUrl()}${normalized}.md`
}

/**
 * Canonical alternates for a route path, ready to spread into metadata. The
 * `text/markdown` alternate is emitted ONLY for paths that actually have a
 * twin (`hasPublicMarkdownTwin` — the same leaf inventory the serving side
 * uses), so a new page adopting this helper can never advertise a 404.
 */
export function getCanonicalAlternates(path: string) {
  return {
    alternates: {
      canonical: getAbsoluteUrl(path),
      ...(hasPublicMarkdownTwin(path)
        ? { types: { 'text/markdown': getMarkdownAlternateHref(path) } }
        : {}),
    },
  }
}

function getOrganizationId(): string {
  return `${getSiteUrl()}/#organization`
}

function getWebsiteId(): string {
  return `${getSiteUrl()}/#website`
}

/** Footer legal name — the Delaware corporation, not a street address. */
const ORGANIZATION_LEGAL_NAME = 'Brew Emails Inc.'

/**
 * Shared Organization node for standalone JSON-LD and the homepage graph.
 * `address` is a country-only PostalAddress: no verified street HQ exists
 * in this repo. Do not invent `streetAddress`.
 */
export function getOrganizationNode() {
  return {
    ...siteConfig.jsonLd.organization,
    '@id': getOrganizationId(),
    url: getSiteUrl(),
    logo: getAbsoluteUrl('/logo/favicon-dark.svg'),
    legalName: ORGANIZATION_LEGAL_NAME,
    contactPoint: {
      '@type': 'ContactPoint' as const,
      telephone: siteConfig.contact.phone,
      email: siteConfig.contact.email.support,
      contactType: 'customer support',
    },
    address: {
      '@type': 'PostalAddress' as const,
      addressCountry: 'US',
    },
  }
}

/**
 * Generate page metadata from centralized config.
 *
 * Intentionally does NOT set `openGraph.images`. Each route ships a
 * colocated `opengraph-image.tsx` that Next.js automatically wires into
 * the page's metadata as long as no explicit `openGraph.images` array is
 * set. A page that sets `openGraph` (as this does) REPLACES its parents'
 * openGraph, images included: Next merges a file-based image only at the
 * segment that holds the file. So every route that uses this needs an
 * `opengraph-image.tsx` in its own segment (a re-export of the default is
 * fine); without one it ships no og:image at all. Setting `images` here
 * would shadow the colocated file and force every page to share one OG.
 */
interface PageSeo {
  title: string
  description: string
  path: string
  /** Opt out of the '%s | Brew' template (title rendered verbatim). */
  absoluteTitle?: boolean
}

export function getPageMetadata(page: keyof typeof siteConfig.seo.pages) {
  const pageConfig: PageSeo = siteConfig.seo.pages[page]
  const { seo } = siteConfig
  const siteUrl = getSiteUrl()

  return {
    title: pageConfig.absoluteTitle
      ? { absolute: pageConfig.title }
      : pageConfig.title,
    description: pageConfig.description,
    openGraph: {
      type: seo.openGraph.type,
      locale: seo.openGraph.locale,
      siteName: seo.openGraph.siteName,
      title: pageConfig.title,
      description: pageConfig.description,
      url: getAbsoluteUrl(pageConfig.path),
    },
    twitter: {
      card: seo.twitter.card,
      creator: seo.twitter.creator,
      site: seo.twitter.site,
      title: pageConfig.title,
      description: pageConfig.description,
    },
    alternates: {
      canonical: `${siteUrl}${pageConfig.path}`,
      ...(hasPublicMarkdownTwin(pageConfig.path)
        ? {
            types: {
              'text/markdown': getMarkdownAlternateHref(pageConfig.path),
            },
          }
        : {}),
    },
  }
}

/**
 * Generate base metadata for root layout
 */
export function getBaseMetadata() {
  const { seo, name } = siteConfig
  const siteUrl = getSiteUrl()

  return {
    metadataBase: new URL(siteUrl),
    title: seo.title,
    description: seo.description,
    authors: [{ name: 'Brew Team' }],
    creator: name,
    publisher: name,
    openGraph: {
      type: seo.openGraph.type,
      locale: seo.openGraph.locale,
      url: siteUrl,
      siteName: seo.openGraph.siteName,
      title: seo.title.default,
      description: seo.description,
      images: [seo.openGraph.images.default],
    },
    twitter: {
      card: seo.twitter.card,
      creator: seo.twitter.creator,
      site: seo.twitter.site,
    },
    icons: {
      // Flat vector marks (2.5 KB each). The previous favicon{1,2}.svg embedded
      // 1.2 MB raster textures that are invisible at favicon sizes.
      icon: [
        {
          url: '/logo/favicon-light.svg',
          type: 'image/svg+xml',
        },
        {
          url: '/logo/favicon-dark.svg',
          type: 'image/svg+xml',
          media: '(prefers-color-scheme: dark)',
        },
      ],
      apple: [
        {
          url: '/logo/favicon-light.svg',
          type: 'image/svg+xml',
        },
      ],
    },
    robots: {
      index: true,
      follow: true,
      googleBot: {
        index: true,
        follow: true,
        'max-video-preview': -1,
        'max-image-preview': 'large' as const,
        'max-snippet': -1,
      },
    },
    other: {
      'llms-txt': '/llms.txt',
    },
  }
}

/**
 * Generate Organization JSON-LD schema
 */
export function getOrganizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    ...getOrganizationNode(),
  }
}

/**
 * Generate combined JSON-LD graph for home page
 */
export function getHomePageJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      getOrganizationNode(),
      siteConfig.jsonLd.software,
      {
        ...siteConfig.jsonLd.website,
        '@id': getWebsiteId(),
        url: getSiteUrl(),
        publisher: { '@id': getOrganizationId() },
      },
    ],
  }
}

/**
 * Generate FAQ JSON-LD schema from FAQ items
 */
export function getFaqJsonLd(
  faqItems: Array<{ question: string; answer: string | Array<string> }>
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: Array.isArray(item.answer) ? item.answer.join(' ') : item.answer,
      },
    })),
  }
}

/**
 * Generate Product JSON-LD schema for pricing page
 */
export function getPricingJsonLd() {
  const prices = defaultPriceSummary()
  const pricingUrl = getAbsoluteUrl('/pricing')
  // Plans are sold online only. No shipping or return policy applies to a
  // subscription, so those merchant-listing fields stay absent on purpose.
  const availability = 'https://schema.org/OnlineOnly'
  const offers = [
    {
      '@type': 'Offer' as const,
      name: BILLING_PLANS.free.name,
      price: '0',
      priceCurrency: 'USD',
      url: pricingUrl,
      availability,
    },
    {
      '@type': 'Offer' as const,
      name: BILLING_PLANS.growth.name,
      price: String(prices.growth?.amount ?? 99),
      priceCurrency: prices.growth?.currency ?? 'USD',
      url: pricingUrl,
      availability,
    },
    {
      '@type': 'Offer' as const,
      name: BILLING_PLANS.pro.name,
      price: String(prices.pro?.amount ?? 249),
      priceCurrency: prices.pro?.currency ?? 'USD',
      url: pricingUrl,
      availability,
    },
  ]

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Brew',
    url: pricingUrl,
    image: getAbsoluteUrl('/opengraph-image'),
    description: siteConfig.seo.description,
    brand: {
      '@type': 'Brand',
      name: siteConfig.name,
    },
    offers,
  }
}

// =============================================================================
// PROGRAMMATIC SEO JSON-LD GENERATORS
// =============================================================================

/**
 * Generate Product Comparison JSON-LD for comparison pages
 */
export function getComparisonJsonLd(
  slug: string,
  competitorName: string,
  competitorDescription: string
) {
  const baseUrl = siteConfig.url

  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `Brew vs ${competitorName}: AI-Native ESP Comparison`,
    description: competitorDescription,
    // The slug, not the display name: Kit's page lives at brew-vs-convertkit.
    url: `${baseUrl}/compare/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      name: 'Product Comparison',
      numberOfItems: 2,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@type': 'SoftwareApplication',
            name: 'Brew',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            description: siteConfig.seo.description,
            url: baseUrl,
          },
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: {
            '@type': 'SoftwareApplication',
            name: competitorName,
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
          },
        },
      ],
    },
  }
}
